import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";

import {
  addApplication,
  getApplications,
  getRoleFilterOptions,
  getRolePreview,
  getRoles,
  saveRole,
} from "@/api/rolesSkilled";
import { invalidateCompanyWishlistCache } from "@/cache/companyWishlistCache";
import { usePolling } from "@/composables/usePolling";
import { useUserStore } from "@/stores/user";
import { ApiError, type AsyncStatus } from "@/types/api";
import type {
  ApplicationSummary,
  DailyRoleLog,
  DailyRoleScanJob,
  DailyRoleScanResponse,
  DailyRoleScanStatus,
  RoleFilterKey,
  RoleFilterOptions,
  RoleFilters,
  RoleRecord,
  RolesViewState,
  RoleSortDirection,
} from "@/types/rolesSkilled";
import { dailyRolesWorker } from "@/workers/backendJobs";

export const ROLES_PER_PAGE = 20;
export type RoleScanMutation = "idle" | "starting" | "cancelling";

const POLLABLE_SCAN_STATUSES = new Set<DailyRoleScanStatus>([
  "queued",
  "running",
  "cancel_requested",
]);
const TERMINAL_SCAN_STATUSES = new Set<DailyRoleScanStatus>([
  "success",
  "cancelled",
  "failed",
]);
const SCAN_BACKOFF_MS = [2_000, 4_000, 8_000, 16_000] as const;

const EMPTY_FILTERS: RoleFilters = {
  role_title: "",
  company: "",
  location: "",
  deadline: "",
  posting_date: "",
  job_category: "",
  seniority: "",
  company_provides_sponsorship: "",
  role_provides_sponsorship: "",
  role_status: "",
};

function normalizeError(caught: unknown, fallback: string): ApiError {
  if (caught instanceof ApiError) return caught;
  return new ApiError(caught instanceof Error && caught.message ? caught.message : fallback);
}

function isPollableScanStatus(status: DailyRoleScanStatus): boolean {
  return POLLABLE_SCAN_STATUSES.has(status);
}

function isTerminalScanStatus(status: DailyRoleScanStatus): boolean {
  return TERMINAL_SCAN_STATUSES.has(status);
}

function isApplicationSummary(value: unknown): value is ApplicationSummary {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ApplicationSummary>;
  const hasReference =
    (typeof candidate.role_id === "string" && candidate.role_id.trim().length > 0) ||
    (typeof candidate.application_url === "string" && candidate.application_url.trim().length > 0) ||
    (typeof candidate.source_url === "string" && candidate.source_url.trim().length > 0);
  return (
    typeof candidate.id === "string" &&
    candidate.id.trim().length > 0 &&
    typeof candidate.company === "string" &&
    candidate.company.trim().length > 0 &&
    typeof candidate.role_title === "string" &&
    candidate.role_title.trim().length > 0 &&
    hasReference
  );
}

export function createRolesStore() {
  const userStore = useUserStore();
  const roles = ref<RoleRecord[]>([]);
  const applications = ref<ApplicationSummary[]>([]);
  const catalogueFilterOptions = ref<RoleFilterOptions>({
    role_title: [],
    company: [],
    location: [],
    deadline: [],
    posting_date: [],
    job_category: [],
    seniority: [],
    company_provides_sponsorship: [],
    role_provides_sponsorship: [],
    role_status: [],
  });
  const previewMode = ref(false);
  const roleLoadStatus = ref<AsyncStatus>("idle");
  const applicationLoadStatus = ref<AsyncStatus>("idle");
  const isRefreshingRoles = ref(false);
  const isRefreshingApplications = ref(false);
  const roleError = ref<ApiError | null>(null);
  const applicationError = ref<ApiError | null>(null);
  const roleActionError = ref<ApiError | null>(null);
  const scanError = ref<ApiError | null>(null);
  const notice = ref("");
  const filters = reactive<RoleFilters>({ ...EMPTY_FILTERS });
  const sortKey = ref<RoleFilterKey>("company");
  const sortDirection = ref<RoleSortDirection>("asc");
  const page = ref(1);
  const pageSize = ref(ROLES_PER_PAGE);
  const totalRoles = ref(0);
  const pendingRoleIds = ref<string[]>([]);
  const activeScan = ref<DailyRoleScanJob | null>(null);
  const scanLabel = ref("Idle");
  const scanLogs = ref<DailyRoleLog[]>([]);
  const scanMutation = ref<RoleScanMutation>("idle");
  const scanPollFailures = ref(0);

  let sessionGeneration = 0;
  let observationActive = true;
  let roleLoadController: AbortController | null = null;
  let applicationLoadController: AbortController | null = null;
  let roleLoadGeneration = 0;
  let applicationLoadGeneration = 0;
  let rolePreviousStatus: AsyncStatus = "idle";
  let applicationPreviousStatus: AsyncStatus = "idle";
  const roleMutationTokens = new Map<string, symbol>();
  const roleMutationControllers = new Map<string, AbortController>();
  let scanMutationToken: symbol | null = null;
  let scanMutationController: AbortController | null = null;
  let activeScanOwnerId: string | null = null;

  const filteredRoles = computed(() => roles.value);
  const filterOptions = computed<RoleFilterOptions>(() => catalogueFilterOptions.value);
  const pageCount = computed(() => Math.max(1, Math.ceil(totalRoles.value / pageSize.value)));
  const visibleRoles = computed(() => roles.value);
  const linkedApplicationByRoleId = computed(() => {
    const linked = new Map<string, ApplicationSummary>();
    const applicationsByRoleId = new Map(
      applications.value
        .filter((application) => application.role_id)
        .map((application) => [application.role_id as string, application]),
    );
    for (const role of roles.value) {
      const direct = applicationsByRoleId.get(role.id);
      if (direct) {
        linked.set(role.id, direct);
        continue;
      }
      const roleUrl = role.application_url || role.source_url;
      if (!roleUrl) continue;
      const fallback = applications.value.find((application) => {
        const applicationUrl = application.application_url || application.source_url;
        return role.company === application.company && roleUrl === applicationUrl;
      });
      if (fallback) linked.set(role.id, fallback);
    }
    return linked;
  });
  const rolesCount = computed(() => roles.value.length);
  const unlinkedRoleCount = computed(() => roles.value.length - linkedApplicationByRoleId.value.size);
  const viewState = computed<RolesViewState>(() => {
    if (roleLoadStatus.value === "loading") return "loading";
    if (roleLoadStatus.value === "error" && !roles.value.length) return "error";
    if (!roles.value.length && Object.values(filters).some((value) => value.trim())) return "no-match";
    if (!roles.value.length) return "empty";
    if (!filteredRoles.value.length) return "no-match";
    if (isRefreshingRoles.value) return "updating";
    return "success";
  });
  const hasActiveScan = computed(() => Boolean(activeScan.value && isPollableScanStatus(activeScan.value.status)));
  const canStartScan = computed(
    () =>
      observationActive &&
      scanMutation.value === "idle" &&
      (!activeScan.value || isTerminalScanStatus(activeScan.value.status)),
  );
  const canCancelScan = computed(
    () => observationActive && scanMutation.value === "idle" && hasActiveScan.value,
  );
  const canRetryScan = computed(() => {
    if (!observationActive || scanMutation.value !== "idle" || !activeScan.value) return false;
    const unknown =
      !isPollableScanStatus(activeScan.value.status) && !isTerminalScanStatus(activeScan.value.status);
    return unknown || scanPollFailures.value >= 5;
  });

  watch(pageCount, (count) => {
    if (page.value > count) page.value = count;
  }, { flush: "sync" });

  function ownsRoleLoad(controller: AbortController, generation: number, session: number): boolean {
    return (
      roleLoadController === controller &&
      roleLoadGeneration === generation &&
      sessionGeneration === session &&
      !controller.signal.aborted
    );
  }

  function ownsApplicationLoad(controller: AbortController, generation: number, session: number): boolean {
    return (
      applicationLoadController === controller &&
      applicationLoadGeneration === generation &&
      sessionGeneration === session &&
      !controller.signal.aborted
    );
  }

  async function loadRoles(signal?: AbortSignal): Promise<void> {
    roleLoadController?.abort();
    const controller = new AbortController();
    roleLoadController = controller;
    const generation = ++roleLoadGeneration;
    const session = sessionGeneration;
    const previousStatus = roleLoadStatus.value === "loading" ? rolePreviousStatus : roleLoadStatus.value;
    rolePreviousStatus = previousStatus;
    const refreshing = roles.value.length > 0 && (previousStatus === "success" || previousStatus === "empty");
    let callerAbortListener: (() => void) | undefined;
    if (signal) {
      callerAbortListener = () => controller.abort();
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", callerAbortListener, { once: true });
    }
    isRefreshingRoles.value = refreshing;
    if (!refreshing) roleLoadStatus.value = "loading";
    roleError.value = null;
    try {
      const [storedResult, filterResult] = await Promise.all([
        getRoles({
          direction: sortDirection.value,
          filters: { ...filters },
          page: page.value,
          page_size: pageSize.value,
          sort: sortKey.value,
        }, controller.signal),
        getRoleFilterOptions(controller.signal).catch(() => null),
      ]);
      if (!ownsRoleLoad(controller, generation, session)) return;
      if (filterResult) catalogueFilterOptions.value = filterResult.options;
      const hasFilters = Object.values(filters).some((value) => value.trim());
      const shouldPreview = !storedResult.roles.length && !hasFilters && (storedResult.total ?? 0) === 0;
      const result = shouldPreview ? await getRolePreview(controller.signal) : storedResult;
      if (!ownsRoleLoad(controller, generation, session)) return;
      previewMode.value = shouldPreview && result.preview === true;
      roles.value = previewMode.value
        ? result.roles.map((role) => ({ ...role, preview: true }))
        : result.roles;
      totalRoles.value = previewMode.value ? roles.value.length : (storedResult.total ?? roles.value.length);
      if (!previewMode.value && typeof storedResult.page === "number") page.value = storedResult.page;
      roleLoadStatus.value = roles.value.length ? "success" : "empty";
    } catch (caught: unknown) {
      if (!ownsRoleLoad(controller, generation, session)) return;
      const normalized = normalizeError(caught, "Roles could not be loaded.");
      roleError.value = normalized;
      roleLoadStatus.value = refreshing ? (roles.value.length ? "success" : "empty") : "error";
      throw normalized;
    } finally {
      if (signal && callerAbortListener) signal.removeEventListener("abort", callerAbortListener);
      if (roleLoadGeneration === generation && sessionGeneration === session) {
        isRefreshingRoles.value = false;
        if (controller.signal.aborted && roleLoadStatus.value === "loading") {
          roleLoadStatus.value = previousStatus;
        }
      }
      if (roleLoadController === controller) roleLoadController = null;
    }
  }

  async function loadApplications(signal?: AbortSignal): Promise<void> {
    applicationLoadController?.abort();
    const controller = new AbortController();
    applicationLoadController = controller;
    const generation = ++applicationLoadGeneration;
    const session = sessionGeneration;
    const previousStatus =
      applicationLoadStatus.value === "loading" ? applicationPreviousStatus : applicationLoadStatus.value;
    applicationPreviousStatus = previousStatus;
    const refreshing =
      applications.value.length > 0 && (previousStatus === "success" || previousStatus === "empty");
    let callerAbortListener: (() => void) | undefined;
    if (signal) {
      callerAbortListener = () => controller.abort();
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", callerAbortListener, { once: true });
    }
    isRefreshingApplications.value = refreshing;
    if (!refreshing) applicationLoadStatus.value = "loading";
    applicationError.value = null;
    try {
      const result = await getApplications(controller.signal);
      if (!ownsApplicationLoad(controller, generation, session)) return;
      applications.value = result.applications;
      applicationLoadStatus.value = applications.value.length ? "success" : "empty";
    } catch (caught: unknown) {
      if (!ownsApplicationLoad(controller, generation, session)) return;
      const normalized = normalizeError(caught, "Applications could not be loaded.");
      applicationError.value = normalized;
      applicationLoadStatus.value = refreshing
        ? (applications.value.length ? "success" : "empty")
        : "error";
      throw normalized;
    } finally {
      if (signal && callerAbortListener) signal.removeEventListener("abort", callerAbortListener);
      if (applicationLoadGeneration === generation && sessionGeneration === session) {
        isRefreshingApplications.value = false;
        if (controller.signal.aborted && applicationLoadStatus.value === "loading") {
          applicationLoadStatus.value = previousStatus;
        }
      }
      if (applicationLoadController === controller) applicationLoadController = null;
    }
  }

  async function load(signal?: AbortSignal): Promise<void> {
    await Promise.allSettled([loadRoles(signal), loadApplications(signal)]);
  }

  function retryRoles(): Promise<void> {
    return loadRoles();
  }

  function retryApplications(): Promise<void> {
    return loadApplications();
  }

  function setFilter(key: RoleFilterKey, value: string): void {
    filters[key] = value;
    page.value = 1;
    void loadRoles();
  }

  function resetFilters(): void {
    Object.assign(filters, EMPTY_FILTERS);
    page.value = 1;
    void loadRoles();
  }

  function setSort(key: RoleFilterKey): void {
    if (sortKey.value === key) sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    else {
      sortKey.value = key;
      sortDirection.value = "asc";
    }
    page.value = 1;
    void loadRoles();
  }

  function setPage(nextPage: number): void {
    const normalized = Math.min(pageCount.value, Math.max(1, Math.trunc(nextPage)));
    if (normalized === page.value) return;
    page.value = normalized;
    void loadRoles();
  }

  function setPageSize(nextPageSize: number): void {
    const normalized = Math.trunc(nextPageSize);
    if (![10, 20, 50].includes(normalized)) return;
    pageSize.value = normalized;
    page.value = 1;
    void loadRoles();
  }

  function applicationFor(role: RoleRecord): ApplicationSummary | undefined {
    return linkedApplicationByRoleId.value.get(role.id);
  }

  function setRolePending(roleId: string, pending: boolean): void {
    const next = new Set(pendingRoleIds.value);
    if (pending) next.add(roleId);
    else next.delete(roleId);
    pendingRoleIds.value = [...next];
  }

  function ownsRoleMutation(roleId: string, token: symbol, session: number): boolean {
    return sessionGeneration === session && roleMutationTokens.get(roleId) === token;
  }

  function replaceRole(roleId: string, replacement: RoleRecord): void {
    roles.value = roles.value.map((role) => (role.id === roleId ? replacement : role));
    previewMode.value = roles.value.some((role) => role.preview === true);
  }

  async function addRole(role: RoleRecord): Promise<ApplicationSummary | null> {
    if (pendingRoleIds.value.includes(role.id)) return null;
    const token = Symbol(role.id);
    const session = sessionGeneration;
    const ownerId = userStore.user?.id ?? null;
    const controller = new AbortController();
    roleMutationTokens.set(role.id, token);
    roleMutationControllers.set(role.id, controller);
    setRolePending(role.id, true);
    roleActionError.value = null;
    notice.value = "";
    let storedRole = role;
    let savedPreview = false;
    try {
      if (role.preview) {
        storedRole = (await saveRole(role, controller.signal)).role;
        savedPreview = true;
        if (
          !ownsRoleMutation(role.id, token, session) ||
          (userStore.user?.id ?? null) !== ownerId
        ) return null;
        replaceRole(role.id, storedRole);
        invalidateCompanyWishlistCache(ownerId);
      }
      const result = await addApplication(storedRole.id, controller.signal);
      if (
        !ownsRoleMutation(role.id, token, session) ||
        (userStore.user?.id ?? null) !== ownerId
      ) return null;
      if (!savedPreview) invalidateCompanyWishlistCache(ownerId);
      applications.value = [
        ...applications.value.filter((application) => application.id !== result.application.id),
        result.application,
      ];
      applicationLoadStatus.value = applications.value.length ? "success" : "empty";
      notice.value = "Added to applications";
      return result.application;
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, "The role could not be added to applications.");
      if (ownsRoleMutation(role.id, token, session)) {
        if (savedPreview) replaceRole(role.id, storedRole);
        roleActionError.value = normalized;
      }
      throw normalized;
    } finally {
      if (ownsRoleMutation(role.id, token, session)) {
        roleMutationTokens.delete(role.id);
        roleMutationControllers.delete(role.id);
        setRolePending(role.id, false);
      }
    }
  }

  function mergeApplications(nextApplications: ApplicationSummary[]): void {
    const merged = new Map(applications.value.map((application) => [application.id, application]));
    for (const application of nextApplications) merged.set(application.id, application);
    applications.value = [...merged.values()];
    applicationLoadStatus.value = applications.value.length ? "success" : "empty";
  }

  function activeScanId(): string {
    const jobId = activeScan.value?.id;
    if (!jobId) throw new ApiError("No Daily Roles scan is active.");
    return jobId;
  }

  function scanStatusLabel(status: DailyRoleScanStatus): string {
    return status
      .replaceAll("_", " ")
      .replace(/^./, (first) => first.toLocaleUpperCase());
  }

  async function applyScanJob(job: DailyRoleScanJob, acceptOwnedStart = false): Promise<boolean> {
    const acceptedId = activeScan.value?.id;
    if (!acceptOwnedStart && (!acceptedId || job.id !== acceptedId)) return false;
    const ownerId = activeScanOwnerId;
    if ((userStore.user?.id ?? null) !== ownerId) return false;
    const session = sessionGeneration;
    activeScan.value = job;
    scanLabel.value = scanStatusLabel(job.status);
    scanLogs.value = job.logs ?? job.result?.scan?.logs ?? [];

    if (isPollableScanStatus(job.status)) {
      scanError.value = null;
      return true;
    }
    polling.stop();
    if (!isTerminalScanStatus(job.status)) {
      scanError.value = new ApiError(`Unsupported Daily Roles scan status: ${job.status}.`);
      return true;
    }
    if (job.status === "failed") {
      scanError.value = new ApiError(job.error || "Daily Roles scan failed.");
      return true;
    }
    scanError.value = null;
    if (job.status === "cancelled") {
      notice.value = "Daily Roles scan cancelled. Stored roles were not changed.";
      return true;
    }

    invalidateCompanyWishlistCache(ownerId);
    const result = job.result;
    if (Array.isArray(result?.roles)) {
      page.value = 1;
      await loadRoles();
      if (sessionGeneration !== session) return false;
    }
    notice.value = result?.scan?.last_scan_at
      ? `Last scan: ${result.scan.last_scan_at}. Found ${result.scan.found ?? 0} roles.`
      : "Daily Roles scan completed.";
    const wishlist = result?.wishlist_applications;
    const expectedCount = result?.scan?.wishlist_added;
    const complete =
      Array.isArray(wishlist) &&
      (typeof expectedCount !== "number" || wishlist.length >= expectedCount) &&
      wishlist.every(isApplicationSummary);
    if (complete) mergeApplications(wishlist);
    else {
      try {
        await loadApplications();
      } catch (caught: unknown) {
        if (sessionGeneration === session) {
          applicationError.value = normalizeError(
            caught,
            "Applications could not be refreshed after the scan.",
          );
        }
      }
    }
    return sessionGeneration === session;
  }

  const polling = usePolling<DailyRoleScanResponse>({
    operation: (signal) => dailyRolesWorker.read(activeScanId(), signal),
    onResult: async ({ job }) => {
      scanPollFailures.value = 0;
      await applyScanJob(job);
    },
    onFailure: (caught, failures) => {
      scanError.value = normalizeError(caught, "Daily Roles scan status is unavailable.");
      scanPollFailures.value = failures;
    },
    nextDelay: () =>
      activeScan.value && isPollableScanStatus(activeScan.value.status) ? 2_000 : null,
    retryDelay: (_caught, failures) =>
      failures < 5
        ? (SCAN_BACKOFF_MS[Math.min(failures - 1, 3)] ?? 16_000)
        : null,
  });

  function ownsScanMutation(token: symbol, session: number): boolean {
    return sessionGeneration === session && scanMutationToken === token;
  }

  async function startScan(): Promise<DailyRoleScanJob | null> {
    if (!canStartScan.value) return null;
    const token = Symbol("start-scan");
    const session = sessionGeneration;
    const ownerId = userStore.user?.id ?? null;
    const controller = new AbortController();
    polling.stop();
    scanMutationToken = token;
    scanMutationController = controller;
    scanMutation.value = "starting";
    scanLabel.value = "Starting";
    scanLogs.value = [];
    scanError.value = null;
    notice.value = "";
    try {
      const { job } = await dailyRolesWorker.start(controller.signal);
      if (!ownsScanMutation(token, session) || (userStore.user?.id ?? null) !== ownerId) return null;
      activeScanOwnerId = ownerId;
      await applyScanJob(job, true);
      if (!ownsScanMutation(token, session)) return null;
      if (observationActive && isPollableScanStatus(job.status)) polling.start(2_000);
      return job;
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, "Daily Roles scan could not be started.");
      if (ownsScanMutation(token, session)) scanError.value = normalized;
      throw normalized;
    } finally {
      if (ownsScanMutation(token, session)) {
        scanMutationToken = null;
        scanMutationController = null;
        scanMutation.value = "idle";
      }
    }
  }

  async function cancelScan(): Promise<DailyRoleScanJob | null> {
    const jobId = activeScan.value?.id;
    if (!jobId || !canCancelScan.value) return null;
    const token = Symbol("cancel-scan");
    const session = sessionGeneration;
    const controller = new AbortController();
    polling.stop();
    scanMutationToken = token;
    scanMutationController = controller;
    scanMutation.value = "cancelling";
    scanLabel.value = "Stopping";
    scanError.value = null;
    try {
      const { job } = await dailyRolesWorker.cancel(jobId, controller.signal);
      if (!ownsScanMutation(token, session)) return null;
      const accepted = await applyScanJob(job);
      if (!accepted && ownsScanMutation(token, session)) {
        scanError.value = new ApiError("Daily Roles cancellation returned a different scan job.");
      }
      if (
        ownsScanMutation(token, session) &&
        observationActive &&
        activeScan.value &&
        isPollableScanStatus(activeScan.value.status)
      ) {
        polling.start(2_000);
      }
      return accepted ? job : activeScan.value;
    } catch (caught: unknown) {
      const normalized = normalizeError(caught, "Daily Roles scan could not be cancelled.");
      if (ownsScanMutation(token, session)) {
        scanError.value = normalized;
        if (observationActive && activeScan.value && isPollableScanStatus(activeScan.value.status)) {
          polling.start(2_000);
        }
      }
      throw normalized;
    } finally {
      if (ownsScanMutation(token, session)) {
        scanMutationToken = null;
        scanMutationController = null;
        scanMutation.value = "idle";
      }
    }
  }

  async function retryScan(): Promise<void> {
    if (!observationActive || !activeScan.value || scanMutation.value !== "idle") return;
    scanError.value = null;
    scanPollFailures.value = 0;
    polling.stop();
    try {
      await polling.runNow();
    } catch {
      // The polling controller owns visible retry errors and failure counts.
    }
  }

  function activate(): void {
    observationActive = true;
    if (activeScan.value && isPollableScanStatus(activeScan.value.status)) polling.start(2_000);
  }

  function abortLoads(): void {
    roleLoadGeneration += 1;
    applicationLoadGeneration += 1;
    roleLoadController?.abort();
    applicationLoadController?.abort();
    roleLoadController = null;
    applicationLoadController = null;
    if (roleLoadStatus.value === "loading") roleLoadStatus.value = rolePreviousStatus;
    if (applicationLoadStatus.value === "loading") applicationLoadStatus.value = applicationPreviousStatus;
    isRefreshingRoles.value = false;
    isRefreshingApplications.value = false;
  }

  function dispose(): void {
    observationActive = false;
    polling.dispose();
    abortLoads();
    scanMutationController?.abort();
    scanMutationController = null;
    scanMutationToken = null;
    scanMutation.value = "idle";
    for (const controller of roleMutationControllers.values()) controller.abort();
    roleMutationControllers.clear();
    roleMutationTokens.clear();
    pendingRoleIds.value = [];
  }

  function reset(): void {
    sessionGeneration += 1;
    dispose();
    roles.value = [];
    applications.value = [];
    catalogueFilterOptions.value = {
      role_title: [],
      company: [],
      location: [],
      deadline: [],
      posting_date: [],
      job_category: [],
      seniority: [],
      company_provides_sponsorship: [],
      role_provides_sponsorship: [],
      role_status: [],
    };
    previewMode.value = false;
    roleLoadStatus.value = "idle";
    applicationLoadStatus.value = "idle";
    rolePreviousStatus = "idle";
    applicationPreviousStatus = "idle";
    roleError.value = null;
    applicationError.value = null;
    roleActionError.value = null;
    scanError.value = null;
    notice.value = "";
    Object.assign(filters, EMPTY_FILTERS);
    sortKey.value = "company";
    sortDirection.value = "asc";
    page.value = 1;
    pageSize.value = ROLES_PER_PAGE;
    totalRoles.value = 0;
    activeScan.value = null;
    activeScanOwnerId = null;
    scanLabel.value = "Idle";
    scanLogs.value = [];
    scanPollFailures.value = 0;
  }

  return {
    roles,
    applications,
    previewMode,
    roleLoadStatus,
    applicationLoadStatus,
    isRefreshingRoles,
    isRefreshingApplications,
    roleError,
    applicationError,
    roleActionError,
    scanError,
    notice,
    filters,
    filterOptions,
    sortKey,
    sortDirection,
    page,
    pageSize,
    totalRoles,
    pendingRoleIds,
    activeScan,
    scanLabel,
    scanLogs,
    scanMutation,
    scanPollFailures,
    filteredRoles,
    visibleRoles,
    pageCount,
    rolesCount,
    unlinkedRoleCount,
    linkedApplicationByRoleId,
    viewState,
    hasActiveScan,
    canStartScan,
    canCancelScan,
    canRetryScan,
    load,
    loadRoles,
    loadApplications,
    retryRoles,
    retryApplications,
    setFilter,
    resetFilters,
    setSort,
    setPage,
    setPageSize,
    applicationFor,
    addRole,
    startScan,
    cancelScan,
    retryScan,
    activate,
    dispose,
    reset,
  };
}

export const useRolesStore = defineStore("roles", createRolesStore);
export type RolesStore = ReturnType<typeof useRolesStore>;
